import React, { useEffect, useRef, useState } from 'react'

type A11yClass =
  | 'a11y-large-text'
  | 'a11y-contrast'
  | 'a11y-grayscale'
  | 'a11y-underline'
  | 'a11y-readable-font'

type Position = { x: number; y: number }

interface Toggle {
  key: A11yClass
  label: string
}

const TOGGLES: Toggle[] = [
  { key: 'a11y-large-text', label: 'Bigger Text' },
  { key: 'a11y-contrast', label: 'High Contrast' },
  { key: 'a11y-grayscale', label: 'Grayscale' },
  { key: 'a11y-underline', label: 'Underline Links' },
  { key: 'a11y-readable-font', label: 'Readable Font' },
]

const POSITION_KEY = 'debeach-accessibility-position'
const MOBILE_POSITION_KEY = 'debeach-accessibility-mobile-position'
const BUTTON_SIZE = 44
const EDGE_GAP = 16
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const AccessibilityWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState<Position | null>(null)
  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  const dragRef = useRef({ pointerId: -1, offsetX: 0, offsetY: 0 })
  const draggedRef = useRef(false)
  const positionRef = useRef<Position | null>(null)
  const [active, setActive] = useState<Record<A11yClass, boolean>>({
    'a11y-large-text': false,
    'a11y-contrast': false,
    'a11y-grayscale': false,
    'a11y-underline': false,
    'a11y-readable-font': false,
  })

  useEffect(() => {
    const root = document.documentElement
    for (const t of TOGGLES) root.classList.toggle(t.key, active[t.key])
  }, [active])

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)')
    const sync = () => {
      const desktop = media.matches
      const size = { width: window.innerWidth, height: window.innerHeight }
      const minY = desktop ? 72 : 124
      const maxY = Math.max(minY, size.height - BUTTON_SIZE - (desktop ? EDGE_GAP : 80))
      const defaultPosition = !desktop
        ? { x: EDGE_GAP, y: minY }
        : { x: size.width - BUTTON_SIZE - EDGE_GAP, y: size.height / 2 - BUTTON_SIZE / 2 }
      const positionKey = desktop ? POSITION_KEY : MOBILE_POSITION_KEY
      setViewport(size)
      let saved: Position | null = null
      try {
        const parsed = JSON.parse(localStorage.getItem(positionKey) || 'null') as Partial<Position> | null
        if (typeof parsed?.x === 'number' && Number.isFinite(parsed.x) && typeof parsed.y === 'number' && Number.isFinite(parsed.y)) saved = parsed as Position
      } catch {
        saved = null
      }
      setPosition((current) => {
        const next = current ?? saved ?? defaultPosition
        const bounded = {
          x: clamp(next.x, EDGE_GAP, size.width - BUTTON_SIZE - EDGE_GAP),
          y: clamp(next.y, minY, maxY),
        }
        positionRef.current = bounded
        return bounded
      })
    }
    sync()
    media.addEventListener('change', sync)
    window.addEventListener('resize', sync)
    return () => {
      media.removeEventListener('change', sync)
      window.removeEventListener('resize', sync)
    }
  }, [])

  const toggle = (key: A11yClass) => setActive((prev) => ({ ...prev, [key]: !prev[key] }))

  const reset = () => {
    setActive({
      'a11y-large-text': false,
      'a11y-contrast': false,
      'a11y-grayscale': false,
      'a11y-underline': false,
      'a11y-readable-font': false,
    })
  }

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!position) return
    dragRef.current = { pointerId: event.pointerId, offsetX: event.clientX - position.x, offsetY: event.clientY - position.y }
    draggedRef.current = false
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDragging || event.pointerId !== dragRef.current.pointerId) return
    const desktop = viewport.width >= 768
    const minY = desktop ? 72 : 124
    const maxY = Math.max(minY, viewport.height - BUTTON_SIZE - (desktop ? EDGE_GAP : 80))
    const next = {
      x: clamp(event.clientX - dragRef.current.offsetX, EDGE_GAP, viewport.width - BUTTON_SIZE - EDGE_GAP),
      y: clamp(event.clientY - dragRef.current.offsetY, minY, maxY),
    }
    if (position && (Math.abs(next.x - position.x) > 3 || Math.abs(next.y - position.y) > 3)) draggedRef.current = true
    positionRef.current = next
    setPosition(next)
  }

  const onPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!isDragging || event.pointerId !== dragRef.current.pointerId) return
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    if (positionRef.current) localStorage.setItem(viewport.width >= 768 ? POSITION_KEY : MOBILE_POSITION_KEY, JSON.stringify(positionRef.current))
  }

  const widgetStyle = position
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : { left: EDGE_GAP, top: 124 }
  const dialogStyle = position
    ? {
        left: clamp(position.x - 180, EDGE_GAP, Math.max(EDGE_GAP, viewport.width - 224 - EDGE_GAP)),
        top: clamp(position.y > viewport.height / 2 ? position.y - 352 : position.y + BUTTON_SIZE + 12, EDGE_GAP, Math.max(EDGE_GAP, viewport.height - 336)),
      }
    : undefined

  return (
    <div data-draggable-accessibility className="fixed z-[90] h-11 w-11" style={widgetStyle}>
      <button
        type="button"
        data-touch-accent
        onClick={() => {
          if (draggedRef.current) {
            draggedRef.current = false
            return
          }
          setIsOpen((open) => !open)
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close accessibility options' : 'Open accessibility options'}
        title="Drag to move accessibility control"
        className={`relative z-20 flex h-11 w-11 touch-none cursor-grab items-center justify-center rounded-full bg-accent-gold text-primary-ink shadow-lg transition-[background-color,box-shadow] duration-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-accent-gold/60 focus:ring-offset-2 focus:ring-offset-primary-ink ${isDragging ? 'cursor-grabbing shadow-2xl' : ''}`}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" aria-hidden="true">
          <circle cx="12" cy="4.5" r="2" />
          <path d="M20.5 8.5h-17a1 1 0 010-2h17a1 1 0 010 2zM11 9.5h2V15l2.2 6a1 1 0 01-1.86.74L11.6 16h-.0l-1.74 5.74a1 1 0 01-1.86-.74L10.2 15V9.5h.8z" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Accessibility options"
          className="fixed z-10 max-h-[calc(100dvh-2rem)] w-56 overflow-y-auto border border-white/15 bg-primary-ink p-4 text-white shadow-2xl"
          style={dialogStyle}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="font-display text-[12px] uppercase tracking-[0.25em] text-accent-gold">Accessibility</p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close accessibility options"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 text-white/75 transition-colors hover:border-accent-gold hover:bg-white/10 hover:text-accent-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-gold"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <ul className="space-y-1">
            {TOGGLES.map((t) => (
              <li key={t.key}>
                <button type="button" onClick={() => toggle(t.key)} aria-pressed={active[t.key]} className={`flex min-h-11 w-full items-center justify-between px-3 py-2 text-sm transition-colors ${active[t.key] ? 'bg-accent-gold text-primary-ink' : 'bg-white/5 text-white/85 hover:bg-white/10'}`}>
                  <span>{t.label}</span>
                  <span aria-hidden="true" className="text-xs font-bold">{active[t.key] ? 'ON' : 'OFF'}</span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={reset} className="mt-3 min-h-11 w-full border border-white/25 px-3 py-2 font-display text-[11px] uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-accent-gold hover:text-accent-gold">Reset All</button>
        </div>
      )}
    </div>
  )
}

export default AccessibilityWidget
